
  /* ================================
     AJAX FILTER — instant skeleton + prefetch
     - URL never changes
     - Hover/touchstart prefetch (100-300ms head start)
     - Idle prefetch of all tabs (skips on 2G / data-saver)
     - USE_AJAX_VIEW is currently FALSE so fetches return the full themed page.
       This is the safest default — guarantees the browser's address bar can
       never end up showing ?view=ajax even if any other code path (t4s native
       Load More handler, etc.) calls pushState with the fetch URL.
       If you flip this back to true for payload size, the
       sanitizeFooterLinks() guard below + the redirect script inside
       templates/blog.ajax.liquid form the second line of defense.

     - CROSS-PAGE PENDING FILTER (added):
       The article-page "Explore Other Topics" section (article-blog-explore.liquid)
       writes the chosen tag key to sessionStorage('blogPendingFilter') and then
       navigates the user to the clean base blog URL (no /tagged/, no query). On
       load, initFromPending() below reads and consumes that key and drives the
       SAME applyFilter() a tab click would — so the filter appears applied while
       the address bar stays clean. Empty key = "All Blogs" (no filter). Cleared
       on read, so a manual refresh resets to All Blogs, matching a native click.
       On landing it also brings the active tab into view in the horizontal track
       (All Blogs -> scroll to start; a topic -> center) so the user can SEE which
       filter is applied even if that tab was off-screen or a stale scroll was saved.
  ================================ */
  (function () {
    'use strict';

    var tabsContainer = document.querySelector('.blog-circle-tabs');
    if (!tabsContainer) return;
    var track = tabsContainer.querySelector('.blog-circle-track');
    if (!track) return;

    var baseBlogUrl = tabsContainer.dataset.blogUrl || '/blogs/blog';
    var USE_AJAX_VIEW = true; // keep false unless blog.ajax.liquid is verified safe
    var HOVER_DELAY = 65; // ms before hover triggers prefetch
    var IDLE_DELAY = 300; // ms after page load before idle prefetch begins
    var IDLE_SPACING = 60; // ms between each idle prefetch (don't slam server)

    var htmlCache = new Map(); // key -> string (resolved) or Promise<string> (in-flight)
    var pendingUrl = null; // most recently clicked URL — used to skip stale responses

    function normalizePath(u) {
      try {
        var url = new URL(u, window.location.origin);
        return url.pathname + url.search;
      } catch (e) {
        return u;
      }
    }

    function buildFetchUrl(url) {
      if (!USE_AJAX_VIEW) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'view=ajax';
    }

    /* ----------------------------------------------------------------
       LAYER 3 — Footer link sanitiser
       Strips ?view=ajax from any <a href> inside the AJAX-replaced
       blog footer (Load More button, prev link, etc.) before the
       footer is injected into the live DOM.

       Why: t4s native AJAX loader binds to data-load-more anchors
       and uses their href when it calls history.pushState. If a
       href contains view=ajax (whether from this script's fetch URL
       or from any other code path), the address bar inherits it,
       and a subsequent refresh/share lands on the bare template.

       This is defense-in-depth — even if USE_AJAX_VIEW gets flipped
       back to true later, or if a different snippet ever appends
       view=ajax to a footer link, the address bar stays clean.
    ---------------------------------------------------------------- */
    function sanitizeFooterLinks(footerEl) {
      if (!footerEl) return;
      var links = footerEl.querySelectorAll('a[href]');
      for (var i = 0; i < links.length; i++) {
        var rawHref = links[i].getAttribute('href');
        if (!rawHref) continue;
        try {
          var u = new URL(rawHref, window.location.origin);
          if (u.searchParams.has('view')) {
            u.searchParams.delete('view');
            var cleanQuery = u.searchParams.toString();
            var cleanHref = u.pathname + (cleanQuery ? '?' + cleanQuery : '') + u.hash;
            links[i].setAttribute('href', cleanHref);
          }
        } catch (e) {
          /* malformed href — leave as-is */
        }
      }
    }

    function setActiveTab(tab) {
      var all = document.querySelectorAll('.blog-circle-item');
      for (var i = 0; i < all.length; i++) all[i].classList.remove('is-active');
      if (tab) tab.classList.add('is-active');
    }

    function getSkeletonCount(grid) {
      var n = grid && grid.children ? grid.children.length : 6;
      if (!n) n = 6;
      return Math.max(3, Math.min(n, 12));
    }

    function renderSkeleton(grid) {
      var n = getSkeletonCount(grid);
      var html = '';
      for (var i = 0; i < n; i++) {
        html +=
          '<div class="t4s-col-item t4s-post-skeleton">' +
          '<div class="skel-img"></div>' +
          '<div class="skel-line medium"></div>' +
          '<div class="skel-line short"></div>' +
          '<div class="skel-line"></div>' +
          '</div>';
      }
      grid.innerHTML = html;
      grid.classList.add('blog-grid-loading');
    }

    function fetchPage(url) {
      var key = normalizePath(url);
      if (htmlCache.has(key)) {
        var cached = htmlCache.get(key);
        return typeof cached === 'string' ? Promise.resolve(cached) : cached;
      }

      var p = fetch(buildFetchUrl(url), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (text) {
          htmlCache.set(key, text);
          return text;
        })
        .catch(function (err) {
          htmlCache.delete(key);
          throw err;
        });

      htmlCache.set(key, p);
      return p;
    }

    function prefetch(url) {
      fetchPage(url).catch(function () {
        /* silent */
      });
    }

    function applyFilter(url, clickedTab) {
      pendingUrl = url;
      setActiveTab(clickedTab);

      var grid = document.querySelector('[data-contentlm-replace]');
      if (grid) renderSkeleton(grid);

      fetchPage(url)
        .then(function (html) {
          if (pendingUrl !== url) return; // a newer click superseded this one

          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.querySelector('[data-contentlm-replace]');
          var newFooter = doc.querySelector('.t4s-blog-footer');
          var curGrid = document.querySelector('[data-contentlm-replace]');
          var curFooter = document.querySelector('.t4s-blog-footer');

          if (curGrid && newGrid) {
            curGrid.innerHTML = newGrid.innerHTML;
            curGrid.classList.remove('blog-grid-loading');
          }

          /* LAYER 3 — sanitise the new footer BEFORE it enters the live DOM */
          if (newFooter) {
            sanitizeFooterLinks(newFooter);
            if (curFooter) {
              curFooter.outerHTML = newFooter.outerHTML;
            } else {
              var page = document.querySelector('.t4s-main-blog-page');
              if (page) page.insertAdjacentHTML('beforeend', newFooter.outerHTML);
            }
          } else if (curFooter) {
            curFooter.remove();
          }

          /* Also sanitise any footer that may have already been injected
             by t4s native AJAX (e.g. Load More fired before user clicked
             a filter tab). This catches stale view=ajax hrefs already
             present in the live DOM. */
          var liveFooter = document.querySelector('.t4s-blog-footer');
          sanitizeFooterLinks(liveFooter);

          try {
            document.dispatchEvent(new Event('lazyloaded'));
          } catch (e) {}
          try {
            window.dispatchEvent(new Event('scroll'));
          } catch (e) {}
          try {
            window.dispatchEvent(new Event('resize'));
          } catch (e) {}
          if (window.t4sLazy && window.t4sLazy.update) window.t4sLazy.update();
          if (window.lazyloadt4s && window.lazyloadt4s.update) window.lazyloadt4s.update();

          var rect = tabsContainer.getBoundingClientRect();
          if (rect.top < 0) tabsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
        })
        .catch(function (err) {
          if (pendingUrl !== url) return;
          var grid = document.querySelector('[data-contentlm-replace]');
          if (grid) grid.classList.remove('blog-grid-loading');
          console.error('[Blog filter] Fetch failed:', err);
        });
    }

    // ===== Click handler (capture phase) =====
    track.addEventListener(
      'click',
      function (e) {
        var tab = e.target.closest('.blog-circle-item');
        if (!tab) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

        e.preventDefault();
        e.stopPropagation();

        var href = tab.getAttribute('href');
        var isActive = tab.classList.contains('is-active');
        var isAllBlogsTab = normalizePath(href) === normalizePath(baseBlogUrl);

        if (isActive) {
          if (isAllBlogsTab) return;
          var tabs = track.querySelectorAll('.blog-circle-item');
          var allTab = null;
          for (var i = 0; i < tabs.length; i++) {
            if (normalizePath(tabs[i].getAttribute('href')) === normalizePath(baseBlogUrl)) {
              allTab = tabs[i];
              break;
            }
          }
          applyFilter(baseBlogUrl, allTab);
          return;
        }
        applyFilter(href, tab);
      },
      true
    );

    // ===== Hover/touchstart prefetch =====
    var hoverTimers = new WeakMap();

    track.addEventListener('mouseover', function (e) {
      var tab = e.target.closest('.blog-circle-item');
      if (!tab) return;
      var href = tab.getAttribute('href');
      if (!href) return;
      var t = setTimeout(function () {
        prefetch(href);
      }, HOVER_DELAY);
      hoverTimers.set(tab, t);
    });
    track.addEventListener('mouseout', function (e) {
      var tab = e.target.closest('.blog-circle-item');
      if (!tab) return;
      var t = hoverTimers.get(tab);
      if (t) clearTimeout(t);
    });
    // FIX 2 — Desktop: prefetch on mousedown. mousedown fires ~100–150ms before
    // the click completes, so even a direct (un-hovered) click gets a head start.
    track.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      var tab = e.target.closest('.blog-circle-item');
      if (!tab) return;
      var href = tab.getAttribute('href');
      if (href) prefetch(href);
    });
    track.addEventListener(
      'touchstart',
      function (e) {
        var tab = e.target.closest('.blog-circle-item');
        if (!tab) return;
        var href = tab.getAttribute('href');
        if (href) prefetch(href);
      },
      { passive: true }
    );

    // ===== Idle prefetch all tabs (skips on 2G / data-saver) =====
    function shouldIdlePrefetch() {
      var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (!conn) return true;
      if (conn.saveData) return false;
      if (conn.effectiveType && /^(slow-)?2g$/i.test(conn.effectiveType)) return false;
      return true;
    }

    function idlePrefetchAll() {
      if (!shouldIdlePrefetch()) return;
      var tabs = track.querySelectorAll('.blog-circle-item');
      var idx = 0;
      function next() {
        if (idx >= tabs.length) return;
        var href = tabs[idx].getAttribute('href');
        idx++;
        if (href) prefetch(href);
        setTimeout(next, IDLE_SPACING);
      }
      next();
    }

    function scheduleIdle() {
      var ric =
        window.requestIdleCallback ||
        function (cb) {
          return setTimeout(cb, IDLE_DELAY);
        };
      ric(idlePrefetchAll, { timeout: IDLE_DELAY + 2000 });
    }

    if (document.readyState === 'complete') {
      scheduleIdle();
    } else {
      window.addEventListener('load', scheduleIdle);
    }

    /* ================================================================
       CROSS-PAGE PENDING FILTER — consume sessionStorage('blogPendingFilter')
       Set by the article page's "View All" button. Runs once on load and
       drives the SAME applyFilter() a tab click uses, so the URL stays clean.
       - key === null / ''  -> no actionable filter; just reveal the active tab
       - real key on base    -> find the tab whose href ends /tagged/<key> and
                                filter (or filter by URL if no matching tab)
       Consumed (removed) immediately so a manual refresh resets to All Blogs,
       matching how a native tab click behaves (no persistence across refresh).
       Only acts when we're actually on the base blog URL (not a hard /tagged/
       page), so it never fights a server-rendered tagged view.
    ================================================================ */
    function findTabByKey(key) {
      var tabs = track.querySelectorAll('.blog-circle-item');
      var wantPath = normalizePath(baseBlogUrl + '/tagged/' + key);
      for (var i = 0; i < tabs.length; i++) {
        if (normalizePath(tabs[i].getAttribute('href')) === wantPath) return tabs[i];
      }
      return null;
    }

    /* Bring the active tab into the middle of the horizontal track so the user
       can SEE which filter landed — even if that tab was in a "next fold" of the
       scroller. No-op when the track can't scroll (all tabs already visible).
       Clamped so a tab near either end scrolls only as far as it can. */
    function centerTabInTrack(tab) {
      if (!tab || !track) return;
      var maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 0) return; // nothing to scroll — all tabs fit
      var tRect = tab.getBoundingClientRect();
      var bRect = track.getBoundingClientRect();
      var delta = (tRect.left - bRect.left) - (track.clientWidth - tRect.width) / 2;
      var target = track.scrollLeft + delta;
      if (target < 0) target = 0;
      if (target > maxScroll) target = maxScroll;
      track.scrollTo({ left: target, behavior: 'smooth' });
    }

    /* Make sure the currently-active tab is visible in the horizontal track on
       load. All Blogs is the left-most tab -> scroll to start; any topic tab ->
       center it. This overrides a stale saved scroll that could otherwise leave
       the active tab off-screen after a cross-page landing. */
    function revealActiveTab() {
      var active = track.querySelector('.blog-circle-item.is-active');
      if (!active) return;
      if (normalizePath(active.getAttribute('href')) === normalizePath(baseBlogUrl)) {
        try { track.scrollTo({ left: 0, behavior: 'auto' }); } catch (e) { track.scrollLeft = 0; }
      } else {
        centerTabInTrack(active);
      }
    }

    function initFromPending() {
      var key = null;
      try {
        key = sessionStorage.getItem('blogPendingFilter');
      } catch (e) {
        requestAnimationFrame(revealActiveTab); // storage blocked — still fix scroll
        return;
      }

      // consume the key if one was stashed
      if (key !== null) {
        try { sessionStorage.removeItem('blogPendingFilter'); } catch (e) {}
      }

      var onBase = normalizePath(window.location.pathname) === normalizePath(baseBlogUrl);

      // Actionable pending filter: a real tag key AND we're on the base blog URL.
      if (key && onBase) {
        var tab = findTabByKey(key);
        if (tab) {
          applyFilter(tab.getAttribute('href'), tab); // reuses skeleton + fetch + swap
          // Center AFTER layout settles (is-scrollable class from the first IIFE
          // shifts tab positions on desktop). rAF ensures we measure post-reflow.
          requestAnimationFrame(function () { centerTabInTrack(tab); });
          return;
        }
        // No matching tab — honor the filter by URL; nothing to reveal.
        applyFilter(baseBlogUrl + '/tagged/' + key, null);
        return;
      }

      // No actionable pending filter (key null/empty, or we're on a /tagged/ page
      // the server already rendered): just make the active tab visible, overriding
      // any stale saved scroll from earlier browsing.
      requestAnimationFrame(revealActiveTab);
    }

    // Run after first paint so the grid + tabs exist and native scripts are up.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initFromPending);
    } else {
      initFromPending();
    }
  })();